/**
 * AI-Optimized Logger for Cursor Development
 * Designed specifically for AI debugging - structured, contextual, and comprehensive
 */

// AI Logger for frontend
class AILogger {
    constructor() {
        this.debugMode = false; // Set to false to reduce console noise
        this.logs = [];
        this.maxLogs = 100; // Keep only last 100 logs in memory
    }

    setDebugMode(enabled) {
        this.debugMode = enabled;
        if (enabled) {
            console.log('🔧 AI Logger debug mode enabled');
        }
    }

    _addLog(level, message, data = null) {
        const logEntry = {
            timestamp: new Date().toISOString(),
            level,
            message,
            data
        };
        
        this.logs.push(logEntry);
        
        // Keep only last maxLogs entries
        if (this.logs.length > this.maxLogs) {
            this.logs.shift();
        }
    }

    info(message, data = null) {
        this._addLog('info', message, data);
        if (this.debugMode) {
            console.log(`ℹ️ ${message}`, data || '');
        }
    }

    warn(message, data = null) {
        this._addLog('warn', message, data);
        if (this.debugMode) {
            console.warn(`⚠️ ${message}`, data || '');
        }
    }

    error(message, data = null) {
        this._addLog('error', message, data);
        // Always log errors regardless of debug mode
        console.error(`❌ ${message}`, data || '');
    }

    debug(message, data = null) {
        this._addLog('debug', message, data);
        if (this.debugMode) {
            console.log(`🔍 ${message}`, data || '');
        }
    }

    success(message, data = null) {
        this._addLog('success', message, data);
        if (this.debugMode) {
            console.log(`✅ ${message}`, data || '');
        }
    }

    component(componentName, action, data = null) {
        this._addLog('component', `${componentName}: ${action}`, data);
        if (this.debugMode) {
            console.log(`🧩 [${componentName}] ${action}`, data || '');
        }
    }

    userInteraction(action, element = null, data = null) {
        this._addLog('userInteraction', action, { element, ...data });
        if (this.debugMode) {
            console.log(`👆 User Interaction: ${action}`, { element, ...data });
        }
    }

    markPerformance(operation, data = null) {
        this._addLog('performance', operation, data);
        if (this.debugMode) {
            console.log(`⚡ Performance: ${operation}`, data || '');
        }
    }

    measurePerformance(operation) {
        const startTime = performance.now();
        return () => {
            const duration = performance.now() - startTime;
            this._addLog('performance', `${operation} completed`, { duration: `${duration.toFixed(2)}ms` });
            if (this.debugMode) {
                console.log(`⚡ Performance: ${operation} completed in ${duration.toFixed(2)}ms`);
            }
            return duration;
        };
    }

    performance(operation, duration, data = null) {
        this._addLog('performance', operation, { duration: `${duration.toFixed(2)}ms`, ...data });
        if (this.debugMode) {
            console.log(`⚡ Performance: ${operation} - ${duration.toFixed(2)}ms`, data || '');
        }
    }

    // Method to get all logs (useful for debugging)
    getLogs() {
        return this.logs;
    }

    // Method to clear logs
    clearLogs() {
        this.logs = [];
    }

    // Method to export logs
    exportLogs() {
        return JSON.stringify(this.logs, null, 2);
    }
}

// Create singleton instance
const aiLogger = new AILogger();

// Export the logger
export default aiLogger; 